/**
 * Computation Graph Runtime
 *
 * Handles graph creation, computation, and queries
 */

import {
  Graph,
  GraphDefinition,
  Node,
  NodeId,
  DerivationTree,
  isSourceNode,
  isAssumptionNode,
  isComputedNode,
} from './types';

/**
 * Topologically sort nodes for correct computation order
 * Uses Kahn's algorithm
 */
function topologicalSort(definition: GraphDefinition): NodeId[] {
  const nodes = definition.nodes;
  const nodeIds = Object.keys(nodes);

  // Build in-degree map and adjacency list
  const inDegree = new Map<NodeId, number>();
  const adjacency = new Map<NodeId, NodeId[]>();

  for (const id of nodeIds) {
    inDegree.set(id, 0);
    adjacency.set(id, []);
  }

  // Count dependencies
  for (const id of nodeIds) {
    const node = nodes[id];
    if (isComputedNode(node)) {
      inDegree.set(id, node.deps.length);
      for (const dep of node.deps) {
        adjacency.get(dep)?.push(id);
      }
    }
  }

  // Start with nodes that have no dependencies
  const queue: NodeId[] = [];
  for (const [id, degree] of inDegree) {
    if (degree === 0) {
      queue.push(id);
    }
  }

  const sorted: NodeId[] = [];
  while (queue.length > 0) {
    const id = queue.shift()!;
    sorted.push(id);

    for (const dependent of adjacency.get(id) || []) {
      const newDegree = (inDegree.get(dependent) || 0) - 1;
      inDegree.set(dependent, newDegree);
      if (newDegree === 0) {
        queue.push(dependent);
      }
    }
  }

  if (sorted.length !== nodeIds.length) {
    const missing = nodeIds.filter((id) => !sorted.includes(id));
    throw new Error(`Circular dependency detected involving nodes: ${missing.join(', ')}`);
  }

  return sorted;
}

/**
 * Build reverse dependency index
 * Maps each node to the set of nodes that depend on it
 */
function buildDependentsIndex(definition: GraphDefinition): Map<NodeId, Set<NodeId>> {
  const dependents = new Map<NodeId, Set<NodeId>>();

  for (const id of Object.keys(definition.nodes)) {
    dependents.set(id, new Set());
  }

  for (const [id, node] of Object.entries(definition.nodes)) {
    if (isComputedNode(node)) {
      for (const dep of node.deps) {
        dependents.get(dep)?.add(id);
      }
    }
  }

  return dependents;
}

/**
 * Create a new graph instance from a definition
 */
export function createGraph(definition: GraphDefinition): Graph {
  const computeOrder = topologicalSort(definition);
  const dependents = buildDependentsIndex(definition);

  const graph: Graph = {
    definition,
    values: new Map(),
    overrides: new Map(),
    dependents,
    computeOrder,
  };

  // Initial computation
  compute(graph);

  return graph;
}

/**
 * Compute all values in the graph
 * Respects overrides for assumption nodes
 */
export function compute(graph: Graph, overrides?: Map<NodeId, number>): Map<NodeId, number> {
  const { definition, computeOrder } = graph;

  // Apply any new overrides
  if (overrides) {
    for (const [id, value] of overrides) {
      graph.overrides.set(id, value);
    }
  }

  // Compute values in topological order
  for (const id of computeOrder) {
    const node = definition.nodes[id];

    if (isSourceNode(node)) {
      graph.values.set(id, node.value);
    } else if (isAssumptionNode(node)) {
      // Check for override first, then use default
      const value = graph.overrides.get(id) ?? node.defaultValue;
      graph.values.set(id, value);
    } else if (isComputedNode(node)) {
      // Get dependency values
      const depValues = node.deps.map((dep) => {
        const val = graph.values.get(dep);
        if (val === undefined) {
          throw new Error(`Missing dependency ${dep} for node ${id}`);
        }
        return val;
      });

      // Compute the value
      const value = node.compute(...depValues);
      graph.values.set(id, value);
    }
  }

  return graph.values;
}

/**
 * Get a single value from the graph
 */
export function getValue(graph: Graph, nodeId: NodeId): number | undefined {
  return graph.values.get(nodeId);
}

/**
 * Get a node definition
 */
export function getNode(graph: Graph, nodeId: NodeId): Node | undefined {
  return graph.definition.nodes[nodeId];
}

/**
 * Get the dependencies of a node (what it depends on)
 */
export function getDeps(graph: Graph, nodeId: NodeId): NodeId[] {
  const node = graph.definition.nodes[nodeId];
  if (isComputedNode(node)) {
    return [...node.deps];
  }
  return [];
}

/**
 * Get the dependents of a node (what depends on it)
 */
export function getDependents(graph: Graph, nodeId: NodeId): NodeId[] {
  return Array.from(graph.dependents.get(nodeId) || []);
}

/**
 * Get all transitive dependents (everything affected by changing this node)
 */
export function getTransitiveDependents(graph: Graph, nodeId: NodeId): NodeId[] {
  const visited = new Set<NodeId>();
  const queue = [nodeId];

  while (queue.length > 0) {
    const id = queue.shift()!;
    for (const dep of graph.dependents.get(id) || []) {
      if (!visited.has(dep)) {
        visited.add(dep);
        queue.push(dep);
      }
    }
  }

  return Array.from(visited);
}

/**
 * Set an override for an assumption node
 * Returns the list of nodes that need recomputation
 */
export function setOverride(graph: Graph, nodeId: NodeId, value: number): NodeId[] {
  const node = graph.definition.nodes[nodeId];
  if (!isAssumptionNode(node)) {
    throw new Error(`Cannot override non-assumption node: ${nodeId}`);
  }

  graph.overrides.set(nodeId, value);
  compute(graph);

  return getTransitiveDependents(graph, nodeId);
}

/**
 * Clear an override, reverting to default value
 */
export function clearOverride(graph: Graph, nodeId: NodeId): void {
  graph.overrides.delete(nodeId);
  compute(graph);
}

/**
 * Check if a node has an override
 */
export function isOverridden(graph: Graph, nodeId: NodeId): boolean {
  return graph.overrides.has(nodeId);
}

/**
 * Format a number according to its unit
 */
export function formatValue(value: number, unit: string, options?: { decimals?: number; compact?: boolean }): string {
  const { decimals = 0, compact = false } = options || {};

  switch (unit) {
    case 'currency':
      if (compact && Math.abs(value) >= 1_000_000) {
        return `$${(value / 1_000_000).toFixed(1)}M`;
      } else if (compact && Math.abs(value) >= 1_000) {
        return `$${(value / 1_000).toFixed(0)}K`;
      }
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(value);

    case 'percent':
      return `${value.toFixed(decimals)}%`;

    case 'ratio':
      return value.toFixed(decimals + 2);

    case 'years':
      return `${value.toFixed(decimals)} years`;

    default:
      return value.toFixed(decimals);
  }
}

/**
 * Build a derivation tree showing how a value was computed
 */
export function getDerivation(graph: Graph, nodeId: NodeId): DerivationTree | undefined {
  const node = graph.definition.nodes[nodeId];
  const value = graph.values.get(nodeId);

  if (!node || value === undefined) {
    return undefined;
  }

  const tree: DerivationTree = {
    nodeId,
    node,
    value,
    children: [],
  };

  if (isComputedNode(node)) {
    // Build formula with actual values
    let formulaWithValues = node.formula;
    const depValues: Record<string, number> = {};

    for (const dep of node.deps) {
      const depValue = graph.values.get(dep);
      if (depValue !== undefined) {
        depValues[dep] = depValue;
        const depNode = graph.definition.nodes[dep];
        const formatted = formatValue(depValue, depNode?.unit || 'number');
        formulaWithValues = formulaWithValues.replace(
          new RegExp(`\\{${dep}\\}`, 'g'),
          formatted
        );
      }

      // Recursively build child derivations
      const childTree = getDerivation(graph, dep);
      if (childTree) {
        tree.children.push(childTree);
      }
    }

    // Add the result
    const resultFormatted = formatValue(value, node.unit);
    tree.formulaWithValues = `${formulaWithValues} = ${resultFormatted}`;
  }

  return tree;
}

/**
 * Get all assumption nodes (for building adjustment UIs)
 */
export function getAssumptions(graph: Graph): Array<{ id: NodeId; node: Node; value: number; isOverridden: boolean }> {
  const result: Array<{ id: NodeId; node: Node; value: number; isOverridden: boolean }> = [];

  for (const [id, node] of Object.entries(graph.definition.nodes)) {
    if (isAssumptionNode(node)) {
      result.push({
        id,
        node,
        value: graph.values.get(id) ?? node.defaultValue,
        isOverridden: graph.overrides.has(id),
      });
    }
  }

  return result;
}

/**
 * Get all source nodes (for citation display)
 */
export function getSources(graph: Graph): Array<{ id: NodeId; node: Node; value: number }> {
  const result: Array<{ id: NodeId; node: Node; value: number }> = [];

  for (const [id, node] of Object.entries(graph.definition.nodes)) {
    if (isSourceNode(node)) {
      result.push({
        id,
        node,
        value: graph.values.get(id) ?? node.value,
      });
    }
  }

  return result;
}

/**
 * Validate a graph definition
 */
export function validateGraph(definition: GraphDefinition): string[] {
  const errors: string[] = [];
  const nodeIds = new Set(Object.keys(definition.nodes));

  for (const [id, node] of Object.entries(definition.nodes)) {
    if (isComputedNode(node)) {
      for (const dep of node.deps) {
        if (!nodeIds.has(dep)) {
          errors.push(`Node "${id}" depends on unknown node "${dep}"`);
        }
      }
    }

    if (isAssumptionNode(node)) {
      if (node.defaultSource && !nodeIds.has(node.defaultSource)) {
        errors.push(`Node "${id}" has unknown defaultSource "${node.defaultSource}"`);
      }
    }
  }

  // Check for cycles
  try {
    topologicalSort(definition);
  } catch (e) {
    errors.push((e as Error).message);
  }

  return errors;
}
